import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  DraftDiff,
  Menu,
  StatusTag,
  Tabs,
  theme,
  type MenuItem,
  type StatusTagTone,
} from '@agentisai/design-system';
import type { BuilderAgent, DraftRevision } from '@agentisai/shared-types';
import { describeError } from '../lib/describeError';
import { useBuilder, useSetLiveVersion } from '../queries/useBuilder';
import { DRAFT_REV_PARAM, DRAFT_VIEW_PARAM } from './WorkstreamsRailBody';

/**
 * A revision as one text, so a single line diff covers every field. Field order
 * matches the Draft tab's.
 */
export function revisionText(r: DraftRevision): string {
  return [
    `Name: ${r.displayName}`,
    `Description: ${r.description}`,
    `Triggers: ${r.triggers.join(', ')}`,
    `Trust: ${r.trust}`,
    ...(r.forkedFrom ? [`Forked from: ${r.forkedFrom}`] : []),
    '',
    'Eval questions:',
    ...r.evalQuestions.map((q, i) => `${i + 1}. ${q}`),
    '',
    'Soul:',
    r.soul,
  ].join('\n');
}

const muted = {
  margin: 0,
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

const STATUS_LABEL: Record<BuilderAgent['status'], string> = {
  draft: 'Draft',
  private: 'Private',
  shared: 'Shared',
  retired: 'Retired',
};
const STATUS_TONE: Record<BuilderAgent['status'], StatusTagTone> = {
  draft: 'neutral',
  private: 'info',
  shared: 'success',
  retired: 'neutral',
};

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * The builder workstream's centre pane (ADR-0080, AB-2-2): the agent Forge is
 * drafting, read from AgentForge — never copied into the Files rail. Every Forge
 * save is an immutable numbered revision; the history picks one, and the Changes
 * tab diffs it against the one before.
 */
export function DraftPanel({ workstreamId }: { readonly workstreamId: string }) {
  const builder = useBuilder(workstreamId);
  const liveVersion = useSetLiveVersion(workstreamId);
  const [params, setParams] = useSearchParams();

  function setParam(key: string, value: string | null) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  let body: ReactNode;
  const revisions = builder.data?.revisions ?? [];
  const latest = revisions.at(-1);
  // The revision the live version was published from (AB-2-4).
  const liveRevision = builder.data?.versions.find(
    (v) => v.version === builder.data?.agent?.latestApproved,
  )?.sourceRevision;
  if (builder.isLoading) {
    body = <p style={muted}>Loading draft…</p>;
  } else if (builder.isError) {
    body = (
      <p role="alert" style={muted}>
        Couldn't load this draft. We'll retry automatically.
      </p>
    );
  } else if (latest === undefined) {
    body = (
      <p style={muted}>
        No draft yet. Tell Forge what the agent should do — it saves a draft once it knows enough.
      </p>
    );
  } else {
    // The URL is the truth, validated: an unknown `?rev=` falls back to the latest.
    const selected =
      revisions.find((r) => String(r.revision) === params.get(DRAFT_REV_PARAM)) ?? latest;
    const previous = revisions.find((r) => r.revision === selected.revision - 1);
    const view = params.get(DRAFT_VIEW_PARAM) === 'changes' ? 'changes' : 'draft';
    body = (
      <>
        <nav aria-label="Revision history">
          <ol
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.space['1_5'],
              margin: 0,
              padding: 0,
              listStyle: 'none',
            }}
          >
            {[...revisions].reverse().map((r) => {
              const current = r.revision === selected.revision;
              return (
                <li key={r.revision}>
                  <Button
                    size="sm"
                    variant={current ? 'secondary' : 'ghost'}
                    aria-current={current ? 'true' : undefined}
                    onClick={() => setParam(DRAFT_REV_PARAM, String(r.revision))}
                  >
                    Revision {r.revision} · {formatWhen(r.updatedAt)}
                    {r.revision === liveRevision ? ' · live' : ''}
                  </Button>
                </li>
              );
            })}
          </ol>
        </nav>
        <Tabs
          aria-label="Draft view"
          items={[
            { value: 'draft', label: 'Draft' },
            { value: 'changes', label: 'Changes' },
          ]}
          value={view}
          onChange={(v) => setParam(DRAFT_VIEW_PARAM, v === 'changes' ? 'changes' : null)}
        >
          {(active) =>
            active === 'changes' ? (
              <DraftDiff
                before={previous ? revisionText(previous) : ''}
                after={revisionText(selected)}
                aria-label={
                  previous
                    ? `Changes from revision ${previous.revision} to ${selected.revision}`
                    : `Revision ${selected.revision}, the first draft`
                }
              />
            ) : (
              <RevisionView revision={selected} />
            )
          }
        </Tabs>
      </>
    );
  }

  const agent = builder.data?.agent;
  // AB-2-4 — publish / roll back. Shared and retired agents change through the share
  // review (AB-2-5), so only a draft or private agent offers the controls.
  const versions = builder.data?.versions ?? [];
  const live = agent?.latestApproved;
  const ownsLifecycle = agent?.status === 'draft' || agent?.status === 'private';
  const latestIsLive =
    latest !== undefined &&
    versions.some((v) => v.sourceRevision === latest.revision && v.version === live);
  const older = live === undefined ? [] : versions.filter((v) => v.version < live).reverse();
  const busy = liveVersion.isPending;
  let outcome: ReactNode = null;
  if (liveVersion.isError) {
    outcome = (
      <p role="alert" style={muted}>
        {/* A 409's detail says why; auth / network / 5xx get the shared lines. */}
        {describeError(liveVersion.error).userMessage}
      </p>
    );
  } else if (liveVersion.isSuccess) {
    const v = liveVersion.data.data.version;
    outcome = (
      <p role="status" style={muted}>
        {liveVersion.variables === undefined
          ? `Version ${v} is live — only you can add it to your workstreams.`
          : `Rolled back to version ${v}.`}
      </p>
    );
  }
  return (
    <section
      aria-label="Agent draft"
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space[4],
        padding: theme.space[6],
        background: theme.color.neutral.white,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: theme.space[2] }}>
        <h2
          style={{
            margin: 0,
            fontSize: theme.typography.fontSize.titleSm,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.color.neutral.dark,
          }}
        >
          {agent?.displayName ?? 'New agent'}
        </h2>
        <StatusTag tone={STATUS_TONE[agent?.status ?? 'draft']}>
          {STATUS_LABEL[agent?.status ?? 'draft']}
        </StatusTag>
        {live !== undefined && <span style={muted}>Live: version {live}</span>}
        {ownsLifecycle && latest !== undefined && (
          <div style={{ display: 'flex', gap: theme.space[2], marginInlineStart: 'auto' }}>
            {older.length > 0 && (
              <Menu
                aria-label="Roll back to an earlier version"
                placement="bottom-end"
                trigger={
                  <Button size="sm" variant="ghost" disabled={busy}>
                    Roll back
                  </Button>
                }
                items={older.map(
                  (v) =>
                    ({
                      id: `v${v.version}`,
                      label: `Version ${v.version}`,
                      hint: v.sourceRevision ? `revision ${v.sourceRevision}` : undefined,
                      onSelect: () => liveVersion.mutate(v.version),
                    }) satisfies MenuItem,
                )}
              />
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={busy || latestIsLive}
              onClick={() => liveVersion.mutate(undefined)}
            >
              {latestIsLive
                ? `Revision ${latest.revision} is live`
                : `Publish revision ${latest.revision}`}
            </Button>
          </div>
        )}
      </header>
      {outcome}
      {body}
    </section>
  );
}
DraftPanel.displayName = 'DraftPanel';

/** One revision's fields, read-only — edits happen only by asking Forge (ADR-0080 §9). */
function RevisionView({ revision: r }: { readonly revision: DraftRevision }) {
  const label = {
    margin: 0,
    fontSize: theme.typography.fontSize.caption,
    fontWeight: theme.typography.fontWeight.semibold,
    letterSpacing: theme.typography.letterSpacing.label,
    textTransform: 'uppercase',
    color: theme.color.neutral.muted,
  } as const;
  const text = {
    margin: 0,
    fontSize: theme.typography.fontSize.bodySm,
    lineHeight: theme.typography.lineHeight.body,
    color: theme.color.neutral.body,
  } as const;
  return (
    <dl style={{ display: 'grid', gap: theme.space[3], margin: 0 }}>
      <div>
        <dt style={label}>Description</dt>
        <dd style={text}>{r.description}</dd>
      </div>
      <div>
        <dt style={label}>Triggers · trust</dt>
        <dd style={text}>
          {r.triggers.join(', ')} · {r.trust}
          {r.forkedFrom ? ` · forked from ${r.forkedFrom}` : ''}
        </dd>
      </div>
      <div>
        <dt style={label}>Eval questions</dt>
        <dd style={text}>
          <ol style={{ margin: 0, paddingInlineStart: theme.space[5] }}>
            {r.evalQuestions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </dd>
      </div>
      <div>
        <dt style={label}>Soul</dt>
        <dd style={{ ...text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.soul}</dd>
      </div>
    </dl>
  );
}
RevisionView.displayName = 'RevisionView';
